import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Money } from '@rental/shared';
import { api } from '../lib/api.js';
import { Badge, Button, Card, Empty, Field, Page, Table, TextInput } from '../components/ui.jsx';

interface Breakdown {
  year: number; month: number;
  rentalIncome: number; additionalIncome: number; totalIncome: number;
  propertyExpenses: number; electricityExpenses: number;
  installationExpenses: number; otherExpenses: number;
  totalExpenses: number; netProfit: number;
  rent: { expected: number; received: number; pending: number; overpaid: number };
  perProperty: { propertyId: string; name: string; rentalIncome: number; expenses: number; netProfit: number }[];
  closed: boolean;
}

export function ReportsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['report', year, month],
    queryFn: () =>
      api<{ breakdown: Breakdown }>(`/api/v1/reports/monthly?year=${year}&month=${month}`),
  });
  const closeMutation = useMutation({
    mutationFn: () => api('/api/v1/reports/close', { method: 'POST', body: { year, month } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['report'] }),
  });

  const b = q.data?.breakdown;

  return (
    <Page
      title="Reports"
      actions={
        <div className="flex items-center gap-2">
          <a
            className="rounded border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
            href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/v1/reports/monthly?year=${year}&month=${month}&format=csv`}
          >Export CSV</a>
          <a
            className="rounded border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
            href={`${import.meta.env.VITE_API_URL ?? 'http://localhost:8787'}/api/v1/reports/monthly?year=${year}&month=${month}&format=xlsx`}
          >Export XLSX</a>
          <Button
            variant={b?.closed ? 'secondary' : 'primary'}
            disabled={b?.closed || closeMutation.isPending}
            onClick={() => closeMutation.mutate()}
          >
            {b?.closed ? 'Closed' : closeMutation.isPending ? 'Closing…' : 'Close month'}
          </Button>
        </div>
      }
    >
      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-3 md:items-end">
          <Field label="Year">
            <TextInput type="number" value={year} min={2000} max={2100}
              onChange={(e) => setYear(Number(e.target.value))} />
          </Field>
          <Field label="Month">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full rounded border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
              {monthNames.map((n, i) => (<option key={n} value={i + 1}>{n}</option>))}
            </select>
          </Field>
          <div className="text-right">
            {b?.closed && <Badge tone="good">Closed snapshot</Badge>}
          </div>
        </div>
      </Card>

      {!b ? <Empty>Loading…</Empty> : (
        <Card>
          <Table>
            <thead className="text-left text-slate-500 text-xs">
              <tr>
                <th className="border-b px-2 py-1 dark:border-slate-700">Section</th>
                <th className="border-b px-2 py-1 dark:border-slate-700">Line</th>
                <th className="border-b px-2 py-1 dark:border-slate-700 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                ['Income', 'Rental income',      b.rentalIncome],
                ['Income', 'Additional income',  b.additionalIncome],
                ['Income', 'Total income',       b.totalIncome],
                ['Expenses', 'Property',         b.propertyExpenses],
                ['Expenses', 'Electricity',      b.electricityExpenses],
                ['Expenses', 'Installations',    b.installationExpenses],
                ['Expenses', 'Other',            b.otherExpenses],
                ['Expenses', 'Total expenses',   b.totalExpenses],
                ['Result',   'Net profit',       b.netProfit],
                ['Rent',     'Expected',         b.rent.expected],
                ['Rent',     'Received',         b.rent.received],
                ['Rent',     'Pending',          b.rent.pending],
              ].map(([section, line, amount], idx) => (
                <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1 text-slate-500">{section}</td>
                  <td className="px-2 py-1">{line}</td>
                  <td className="px-2 py-1 text-right">{Money.fromPaise(Number(amount)).format()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
}

const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
